import React, { useEffect, useMemo, useState } from 'react';
import { FaChartLine, FaShieldAlt, FaUsers } from 'react-icons/fa';
import { motion } from 'framer-motion';
import userService from '../../services/userService';
import SearchBar from '../Shared/SearchBar';

const formatDate = (value) => {
  if (!value) return 'N/A';
  return new Date(value).toLocaleString();
};

const formatCurrency = (value) => `Rs ${(value || 0).toFixed(2)}`;

const ReferralReport = () => {
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadReport = async () => {
      try {
        setLoading(true);
        const data = await userService.getReferralReport();
        setRows(data || []);
      } catch (reportError) {
        console.error('Failed to fetch referral report', reportError);
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, []);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((row) =>
      [
        row.referrerName,
        row.referrerUsername,
        row.referredName,
        row.referredUsername,
        row.referralCode,
        row.blockReason
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term))
    );
  }, [rows, searchTerm]);

  const approvedRows = filteredRows.filter((row) => !row.rewardBlocked);
  const blockedRows = filteredRows.filter((row) => row.rewardBlocked);
  const totalPaid = approvedRows.reduce((sum, row) => sum + (row.bonusAmount || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <header className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="flex items-center text-2xl font-extrabold text-gray-900">
              <FaChartLine className="mr-3 text-emerald-600" />
              Referral Report
            </h1>
            <p className="mt-2 text-gray-600">Admin view for referral rewards, blocked attempts, and share performance.</p>
          </div>
          <div className="w-full max-w-md">
            <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} placeholder="Search referrer, referred user or code" />
          </div>
        </header>

        <div className="grid gap-4 md:grid-cols-3 mb-6">
          <div className="rounded-2xl bg-white p-5 shadow-sm border border-gray-100">
            <p className="text-sm text-gray-500">Approved referrals</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">{approvedRows.length}</p>
          </div>
          <div className="rounded-2xl bg-white p-5 shadow-sm border border-gray-100">
            <p className="text-sm text-gray-500">Blocked attempts</p>
            <p className="mt-2 text-3xl font-bold text-red-600">{blockedRows.length}</p>
          </div>
          <div className="rounded-2xl bg-white p-5 shadow-sm border border-gray-100">
            <p className="text-sm text-gray-500">Total paid</p>
            <p className="mt-2 text-3xl font-bold text-emerald-600">{formatCurrency(totalPaid)}</p>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <div className="overflow-x-auto rounded-2xl bg-white shadow-sm border border-gray-100">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-900 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Referrer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Referred User</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Reward</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Security</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <tr key={index}>
                      <td className="px-4 py-4"><div className="h-4 w-36 rounded bg-gray-200 animate-pulse" /></td>
                      <td className="px-4 py-4"><div className="h-4 w-36 rounded bg-gray-200 animate-pulse" /></td>
                      <td className="px-4 py-4"><div className="h-4 w-20 rounded bg-gray-200 animate-pulse" /></td>
                      <td className="px-4 py-4"><div className="h-4 w-24 rounded bg-gray-200 animate-pulse" /></td>
                      <td className="px-4 py-4"><div className="h-4 w-28 rounded bg-gray-200 animate-pulse" /></td>
                      <td className="px-4 py-4"><div className="h-4 w-28 rounded bg-gray-200 animate-pulse" /></td>
                    </tr>
                  ))
                ) : filteredRows.length > 0 ? (
                  filteredRows.map((row) => (
                    <tr key={`${row.referrerUserId}-${row.referredUserId}-${row.rewardedAt}`} className="align-top">
                      <td className="px-4 py-4">
                        <div className="font-semibold text-gray-900">{row.referrerName || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{row.referrerUsername}</div>
                        <div className="text-xs text-gray-400">{row.referrerPhoneNumber}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-semibold text-gray-900">{row.referredName || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{row.referredUsername}</div>
                        <div className="text-xs text-gray-400">{row.referredPhoneNumber}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                          <FaUsers className="mr-2" />
                          {row.referralCode}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {row.rewardBlocked ? (
                          <span className="inline-flex rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700">Blocked</span>
                        ) : (
                          <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                            {formatCurrency(row.bonusAmount)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-start text-sm text-gray-600">
                          <FaShieldAlt className={`mr-2 mt-0.5 ${row.rewardBlocked ? 'text-red-500' : 'text-emerald-500'}`} />
                          <div>
                            <div>{row.rewardBlocked ? row.blockReason || 'Blocked' : 'Passed checks'}</div>
                            <div className="text-xs text-gray-400 mt-1">IP: {row.signupIpHash || 'N/A'}</div>
                            <div className="text-xs text-gray-400">Device: {row.deviceHash || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">{formatDate(row.rewardedAt)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-gray-500">No referral activity found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ReferralReport;
